import type { QueryClient } from '@tanstack/react-query';
import { reconcileDashboardReads } from '@/features/dashboard/realtime-keys';
import { reconcileCalls } from './call-reads';
import { callUUID, captureCallSession, isCurrentCallSession, type CallSession } from './call-session';

const events = new Set(['call:ringing', 'call:accepted', 'call:participant_joined', 'call:participant_left', 'call:rejected', 'call:ended', 'call:timeout']);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
export interface CallHint { type: string; callId: string; eventId: string }

/** Only the native directed envelope can trigger reconciliation. IDs never
 * authorize a prompt, roster mutation, token or media; owned HTTP does that. */
export function readCallHint(value: unknown, owner: Pick<CallSession, 'user' | 'tenant'>): CallHint | null {
  if (!callUUID(owner.user) || !callUUID(owner.tenant) || !record(value) ||
      typeof value.type !== 'string' || !events.has(value.type) || value.tenant_id !== owner.tenant || value.user_id !== owner.user || !record(value.data)) return null;
  const data = value.data;
  if (Object.keys(data).length !== 4 || data.version !== 1 || data.tenant_id !== owner.tenant || !callUUID(data.call_id) || !callUUID(data.event_id)) return null;
  return { type: value.type, callId: data.call_id, eventId: data.event_id };
}

interface HintState { owner: CallSession; seen: Set<string>; running: boolean; dirty: boolean }
const states = new WeakMap<QueryClient, HintState>();

export function receiveCallHint(qc: QueryClient, value: unknown): boolean {
  const owner = captureCallSession(), hint = readCallHint(value, owner);
  if (!hint) return false;
  let state = states.get(qc);
  if (!state || !isCurrentCallSession(state.owner)) {
    state = { owner, seen: new Set(), running: false, dirty: false };
    states.set(qc, state);
  }
  const key = hint.type + ':' + hint.callId + ':' + hint.eventId;
  if (state.seen.has(key)) return true;
  state.seen.add(key);
  if (state.seen.size > 256) state.seen.delete(state.seen.values().next().value!);
  state.dirty = true;
  void reconcileDashboardReads(qc, 'calls');
  if (state.running) return true;
  state.running = true;
  const owned = state;
  // A hint received during a read schedules a subsequent read. Do not cancel
  // each other in a burst or lose the last transition through fetch deduplication.
  void (async () => {
    try {
      while (owned.dirty && isCurrentCallSession(owned.owner)) {
        owned.dirty = false;
        try { await reconcileCalls(qc, captureCallSession()); } catch { /* Poll/focus/reconnect recover reads; no POST replay. */ }
      }
    } finally { owned.running = false; }
  })();
  return true;
}
